import { useEffect, useState } from 'react';

export interface PreviewFrame {
  /** Instant de la frame, en secondes. */
  time: number;
  dataUrl: string;
}

interface FramePreview {
  /** Poster = première image « non noire », pour ne pas afficher un aperçu noir. */
  poster: string | null;
  /** Dix images clés réparties sur la durée, pour le survol (scrubbing). */
  frames: PreviewFrame[];
}

/** En deçà de cette luminance moyenne (sur 255), une frame est considérée « noire ». */
const BLACK_LUMINANCE = 24;
/** Largeur des vignettes échantillonnées : assez pour l'aperçu, assez petit pour rester léger. */
const THUMB_WIDTH = 320;

/**
 * Échantillonne des images d'une vidéo, hors écran, pour en tirer un poster non noir et une bande
 * de vignettes de survol.
 *
 * On charge une **seconde** balise vidéo (masquée) plutôt que de manipuler le lecteur que
 * l'utilisateur voit : le défilement d'échantillonnage ne doit pas parasiter sa lecture. Le dessin
 * sur canvas exige `crossOrigin=anonymous` et des en-têtes CORS côté stockage (déjà en place pour
 * l'extraction d'images).
 */
export function useFramePreview(sourceUrl: string | null): FramePreview {
  const [preview, setPreview] = useState<FramePreview>({ poster: null, frames: [] });

  useEffect(() => {
    if (!sourceUrl) {
      setPreview({ poster: null, frames: [] });
      return;
    }

    let cancelled = false;
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.preload = 'auto';
    video.src = sourceUrl;

    /**
     * Coupe la source du sampler pour stopper tout téléchargement en cours.
     *
     * Sans cela, avec `preload='auto'` la balise masquée continuait de télécharger tout le fichier
     * en tâche de fond **après** l'échantillonnage, jusqu'au démontage — elle entrait en
     * concurrence de bande passante avec le lecteur visible et faisait saccader la lecture d'une
     * grosse vidéo une fois le tampon initial épuisé.
     */
    const release = () => {
      video.removeAttribute('src');
      video.load();
    };

    const seekTo = (time: number): Promise<void> =>
      new Promise((resolve) => {
        const onSeeked = () => {
          video.removeEventListener('seeked', onSeeked);
          resolve();
        };
        video.addEventListener('seeked', onSeeked);
        video.currentTime = time;
      });

    const run = async () => {
      await new Promise<void>((resolve, reject) => {
        video.addEventListener('loadeddata', () => resolve(), { once: true });
        video.addEventListener('error', () => reject(new Error('load')), { once: true });
      });
      if (cancelled) return;

      const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
      if (duration === 0) return;

      const width = THUMB_WIDTH;
      const height = Math.max(1, Math.round((video.videoHeight / video.videoWidth) * width));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;

      // Dix instants répartis sur la durée pour la bande de survol.
      const frameTimes = Array.from({ length: 10 }, (_, i) => (duration * (i + 0.5)) / 10);
      // Sondes rapprochées au début pour trouver la première image non noire (poster).
      const posterProbes = [0, 0.3, 0.6, 1, 1.5, 2, 3].filter((t) => t < duration);
      const times = [...new Set([...posterProbes, ...frameTimes])].sort((a, b) => a - b);

      const captured = new Map<number, { dataUrl: string; luminance: number }>();
      for (const t of times) {
        if (cancelled) return;
        await seekTo(t);
        if (cancelled) return;
        ctx.drawImage(video, 0, 0, width, height);
        captured.set(t, {
          dataUrl: canvas.toDataURL('image/jpeg', 0.7),
          luminance: meanLuminance(ctx, width, height),
        });
      }

      // Poster : première sonde assez lumineuse ; à défaut, la plus lumineuse de toutes.
      let poster: string | null = null;
      for (const t of posterProbes) {
        const c = captured.get(t);
        if (c && c.luminance >= BLACK_LUMINANCE) {
          poster = c.dataUrl;
          break;
        }
      }
      if (!poster) {
        let best = -1;
        for (const c of captured.values()) {
          if (c.luminance > best) {
            best = c.luminance;
            poster = c.dataUrl;
          }
        }
      }

      const frames: PreviewFrame[] = frameTimes.map((t) => ({
        time: t,
        dataUrl: captured.get(t)!.dataUrl,
      }));

      if (!cancelled) setPreview({ poster, frames });
    };

    void run()
      .catch(() => {
        // Échantillonnage impossible (CORS, format) : on reste sans aperçu, sans casser la page.
      })
      .finally(() => {
        // Dès l'échantillonnage terminé (ou échoué), on libère la source : plus de téléchargement
        // en tâche de fond qui priverait le lecteur de bande passante.
        release();
      });

    return () => {
      cancelled = true;
      release();
    };
  }, [sourceUrl]);

  return preview;
}

/** Luminance moyenne (perçue) d'un canvas, sur 255. Sous-échantillonne pour rester rapide. */
function meanLuminance(ctx: CanvasRenderingContext2D, width: number, height: number): number {
  const { data } = ctx.getImageData(0, 0, width, height);
  let sum = 0;
  let count = 0;
  // Un pixel sur 40 (×4 canaux) suffit pour estimer la luminance.
  for (let i = 0; i < data.length; i += 40 * 4) {
    sum += 0.299 * data[i]! + 0.587 * data[i + 1]! + 0.114 * data[i + 2]!;
    count += 1;
  }
  return count > 0 ? sum / count : 0;
}
